// ============================================================================
// FIX-CAPTURE-TRANCHE-2 — the card framework.
//
// One global provider (mounted inside AuthProvider) exposes `openCaptureCards(activityId)`.
// A screen calls it at the POST-SAVE moment of an activity (Adeel D4: never at login, never a
// Home badge). The provider asks the server which cards are due (flag, consent, once-per-field
// and language are all decided server-side in get_capture_cards), shows them one at a time in
// a dialog, records each answer or skip through submit_capture_answer, and disappears.
//
// Offline-safe by construction: if the RPC cannot be reached the farmer simply sees no card —
// the save they just made is never blocked, delayed or errored by this feature. The field stays
// blank and the card is offered again at the next save.
// ============================================================================

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/dialog';
import { useAuth } from '@/features/auth/components/AuthContext';
import { fetchCaptureCards, submitCaptureAnswer, type CaptureAnswer, type CaptureCard, type CaptureLang } from './captureApi';
import { VoicePrompt } from './VoicePrompt';
import { captureStrings } from './captureStrings';

/**
 * FIX-CAPTURE-TRANCHE-2B-W3 (R-CAP-2) — NO CLIP, NO CARD. A question the farmer cannot HEAR is
 * never offered: there is no text fallback. A card is playable only with its own clip, and the
 * NA-11 card also needs its follow-up clip (its second step must not dead-end).
 */
export function isPlayableCard(card: CaptureCard): boolean {
  if (!card.clip_path) return false;
  if (card.atom === 'NA-11' && !card.follow_up_clip_path) return false;
  return true;
}

interface CaptureCardsContextValue {
  /** Offer the due cards for this farmer, right after an activity save. Never throws. */
  openCaptureCards: (activityId?: string | null) => void;
}

const CaptureCardsContext = createContext<CaptureCardsContextValue>({ openCaptureCards: () => undefined });

/** Safe anywhere: without a provider (e.g. isolated screen tests) it is a no-op. */
export const useCaptureCards = () => useContext(CaptureCardsContext);

export function CaptureCardProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [cards, setCards] = useState<CaptureCard[]>([]);
  const [lang, setLang] = useState<CaptureLang>('en');
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const requestSeq = useRef(0);

  const openCaptureCards = useCallback((activityId?: string | null) => {
    const token = user?.sessionToken;
    if (!token) return;
    const seq = ++requestSeq.current;
    void fetchCaptureCards(token, activityId)
      .then((res) => {
        if (seq !== requestSeq.current) return;          // a newer save superseded this one
        if (!res.enabled || res.cards.length === 0) return;
        // W3 (R-CAP-2): drop anything we cannot play, before the dialog ever opens.
        const playable = res.cards.filter(isPlayableCard);
        if (playable.length === 0) return;
        setLang(res.strings);
        setCards(playable);
        setIndex(0);
      })
      .catch((err: unknown) => {
        // Never surface to the farmer: the save already succeeded and this is optional.
        console.warn('[capture] cards unavailable:', err instanceof Error ? err.message : err);
      });
  }, [user?.sessionToken]);

  const advance = useCallback(() => {
    setIndex((i) => {
      const next = i + 1;
      if (next >= cards.length) {
        setCards([]);
        return 0;
      }
      return next;
    });
  }, [cards.length]);

  const record = useCallback(async (answer: CaptureAnswer | null, skipped: boolean) => {
    const token = user?.sessionToken;
    const card = cards[index];
    if (!token || !card) return;
    setBusy(true);
    try {
      await submitCaptureAnswer(token, card.atom, card.subject_ref, answer, skipped, lang);
    } catch (err: unknown) {
      console.warn('[capture] answer not recorded:', err instanceof Error ? err.message : err);
    } finally {
      setBusy(false);
      advance();
    }
  }, [advance, cards, index, lang, user?.sessionToken]);

  const value = useMemo(() => ({ openCaptureCards }), [openCaptureCards]);
  const current = cards[index];
  const s = captureStrings(lang);

  return (
    <CaptureCardsContext.Provider value={value}>
      {children}
      <Dialog open={Boolean(current)} onOpenChange={(open) => { if (!open) setCards([]); }}>
        <DialogContent className="max-w-md" data-testid="capture-dialog" aria-describedby={undefined}>
          {/* Visually hidden title keeps the dialog accessible without adding UI text. */}
          <DialogTitle className="sr-only">{s.header}</DialogTitle>
          {current && (
            <VoicePrompt
              key={`${current.atom}:${current.subject_ref ?? 'farmer'}`}
              card={current}
              busy={busy}
              lang={lang}
              onAnswer={(a) => void record(a, false)}
              // W3: the clip did not resolve or failed to play — drop the card, record NOTHING
              // and move on silently. Never a text fallback.
              onUnavailable={advance}
            />
          )}
        </DialogContent>
      </Dialog>
    </CaptureCardsContext.Provider>
  );
}
